import ReactGA from "react-ga4";


const GA4_ID_REGEX = /^G-[A-Z0-9]+$/i;
const EVENT_NAME_MAX_LEN = 40;
const PARAM_KEY_MAX_LEN = 40;
const PARAM_STRING_MAX_LEN = 100;

let hasInitialized = false;


function getTrackingId() {
  return (process.env.REACT_APP_GA_TRACKING_ID || "").trim();
}


function isProductionEnv() {
  return process.env.NODE_ENV === "production";
}


function isValidGA4TrackingId(trackingId) {
  return GA4_ID_REGEX.test(trackingId);
}


function canTrack() {
  return isProductionEnv() && isValidGA4TrackingId(getTrackingId());
}


function sanitizeEventName(value, fallback = "app_event") {
  let normalized = String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (!normalized) {
    normalized = fallback;
  }
  if (!/^[a-z]/.test(normalized)) {
    normalized = `e_${normalized}`;
  }
  return normalized.slice(0, EVENT_NAME_MAX_LEN);
}


function sanitizeParamKey(value) {
  let normalized = String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (!normalized) {
    return "";
  }
  if (!/^[a-z]/.test(normalized)) {
    normalized = `p_${normalized}`;
  }
  return normalized.slice(0, PARAM_KEY_MAX_LEN);
}


function coerceParamValue(value) {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === "string") {
    return value.slice(0, PARAM_STRING_MAX_LEN);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry)).join(",").slice(0, PARAM_STRING_MAX_LEN);
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value).slice(0, PARAM_STRING_MAX_LEN);
    } catch (err) {
      return undefined;
    }
  }
  return String(value).slice(0, PARAM_STRING_MAX_LEN);
}


function normalizeParams(params = {}) {
  const normalized = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    const normalizedKey = sanitizeParamKey(key);
    const normalizedValue = coerceParamValue(value);
    if (normalizedKey && normalizedValue !== undefined) {
      normalized[normalizedKey] = normalizedValue;
    }
  });
  return normalized;
}


export function initAnalytics() {
  if (!canTrack()) {
    return false;
  }
  if (hasInitialized) {
    return true;
  }

  try {
    ReactGA.initialize(getTrackingId(), {
      gtagOptions: {
        send_page_view: false,
      },
    });
    hasInitialized = true;
    return true;
  } catch (err) {
    return false;
  }
}


function ensureInitialized() {
  if (hasInitialized) {
    return true;
  }
  return initAnalytics();
}


export function trackEvent(name, params = {}) {
  if (!canTrack() || !ensureInitialized()) {
    return false;
  }

  try {
    ReactGA.event(sanitizeEventName(name), normalizeParams(params));
    return true;
  } catch (err) {
    return false;
  }
}


export function trackPageView(pagePath) {
  const defaultPath = window.location.pathname + window.location.search;
  const page_path = String(pagePath || defaultPath);
  const page_location = new URL(page_path, window.location.origin).href;
  const page_title = document.title || "";

  return trackEvent("page_view", {
    page_path,
    page_location,
    page_title,
  });
}


export function trackLogEvent(category, action, label = "") {
  const normalizedCategory = sanitizeEventName(category, "app_event");
  const normalizedAction = sanitizeEventName(action, "unknown_action");

  if (normalizedCategory === "client_error") {
    return trackEvent("client_error", {
      error_context: normalizedAction,
      error_label: label,
      source: "app_log_event",
    });
  }

  if (normalizedCategory === "user_activity") {
    return trackEvent(normalizedAction, {
      source: "app_log_event",
      event_label: label,
    });
  }

  return trackEvent("app_event", {
    event_category: normalizedCategory,
    event_action: normalizedAction,
    event_label: label,
    source: "app_log_event",
  });
}


export function __resetAnalyticsForTests() {
  hasInitialized = false;
}
